import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import {
  listTeachings,
  listFilterOptions,
  type TeachingListFilters,
} from "@/lib/teachings.functions";
import { getMyProfile, listCompletedTeachingIds } from "@/lib/user.functions";

import { TeachingCard } from "@/components/teaching-card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, X } from "lucide-react";

export const Route = createFileRoute("/_authenticated/ensinos/")({
  head: () => ({
    meta: [
      { title: "Catálogo de Ensinos · Beit Tefilah Rechovot" },
      {
        name: "description",
        content:
          "Acervo completo de ensinos da congregação, com filtros por Parashá, data, turno, ano hebraico, palestrante e festividade.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Catálogo de Ensinos · Beit Tefilah Rechovot" },
      {
        property: "og:description",
        content:
          "Filtre os ensinos por Parashá, data, turno, ano hebraico, palestrante e festividade.",
      },
      { name: "twitter:title", content: "Catálogo de Ensinos · Beit Tefilah Rechovot" },
      {
        name: "twitter:description",
        content:
          "Filtre os ensinos por Parashá, data, turno, ano hebraico, palestrante e festividade.",
      },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { q?: string } =>
    typeof search.q === "string" && search.q.trim() ? { q: search.q.trim().slice(0, 200) } : {},
  component: EnsinosPage,
});

const ALL = "__all__";

function EnsinosPage() {
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const optsFn = useServerFn(listFilterOptions);
  const listFn = useServerFn(listTeachings);
  const profileFn = useServerFn(getMyProfile);
  const completedFn = useServerFn(listCompletedTeachingIds);

  const { data: myProfile } = useQuery({ queryKey: ["my-profile"], queryFn: () => profileFn() });
  const defaults = {
    limit: ((myProfile as any)?.pref_items_per_page ?? 24) as number,
    turno: (() => {
      const t = (myProfile as any)?.pref_default_turno;
      return t && t !== "todos" ? (t as "manha" | "tarde") : undefined;
    })(),
  };
  const hideWatched = !!(myProfile as any)?.pref_hide_watched;

  const [filters, setFilters] = useState<TeachingListFilters>({
    order: "recent",
    limit: 24,
    offset: 0,
    search: q,
  });
  const [searchInput, setSearchInput] = useState(q ?? "");

  // Mantém a busca no endereço da página (compartilhável e preservada ao recarregar).
  useEffect(() => {
    setSearchInput(q ?? "");
    setFilters((f) => (f.search === q ? f : { ...f, search: q, offset: 0 }));
  }, [q]);
  const appliedDefaults = useRef(false);

  // Aplica as preferências pessoais do usuário como estado inicial dos filtros.
  useEffect(() => {
    if (!myProfile || appliedDefaults.current) return;
    appliedDefaults.current = true;
    setFilters((f) => ({ ...f, limit: defaults.limit, turno: defaults.turno }));
  }, [myProfile]);

  const { data: options } = useQuery({ queryKey: ["filter-options"], queryFn: () => optsFn() });
  const { data, isLoading } = useQuery({
    queryKey: ["teachings", filters],
    queryFn: () => listFn({ data: filters }),
  });

  const { data: completedIds } = useQuery({
    queryKey: ["completed-teaching-ids"],
    queryFn: () => completedFn(),
    enabled: hideWatched,
  });

  const update = (partial: Partial<TeachingListFilters>) =>
    setFilters((f) => ({ ...f, ...partial, offset: 0 }));

  const clearAll = () => {
    setFilters({ order: "recent", limit: defaults.limit, offset: 0, turno: defaults.turno });
    setSearchInput("");
    navigate({ search: {}, replace: true });
  };

  const completedSet = new Set(completedIds ?? []);
  const visibleRows: any[] = hideWatched
    ? (data?.rows ?? []).filter((t: any) => !completedSet.has(t.id))
    : (data?.rows ?? []);

  const hasFilters = !!(
    filters.search ||
    filters.parashaId ||
    filters.hebrewYear ||
    filters.turno ||
    filters.festivitySlug ||
    filters.gregYear ||
    filters.gregMonth
  );

  const MONTHS_PT = [
    "Janeiro (Tevet/Shevat)",
    "Fevereiro (Shevat/Adar)",
    "Março (Adar/Nissan)",
    "Abril (Nissan/Iyar)",
    "Maio (Iyar/Sivan)",
    "Junho (Sivan/Tammuz)",
    "Julho (Tammuz/Av)",
    "Agosto (Av/Elul)",
    "Setembro (Elul/Tishrei)",
    "Outubro (Tishrei/Cheshvan)",
    "Novembro (Cheshvan/Kislev)",
    "Dezembro (Kislev/Tevet)",
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl font-semibold text-primary">Catálogo de Ensinos</h1>
        <p className="mt-1 text-muted-foreground">
          Filtre por Parashá, data, turno ou festividade. Os filtros são combináveis.
        </p>
      </div>

      <div className="space-y-4 rounded-xl border border-border bg-card p-4">
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const next = searchInput.trim() || undefined;
            update({ search: next });
            navigate({ search: next ? { q: next } : {}, replace: true });
          }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por título..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <Button type="submit">Buscar</Button>
        </form>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Porção da Torá
            </label>
            <Select
              value={filters.parashaId ?? ALL}
              onValueChange={(v) => update({ parashaId: v === ALL ? undefined : v })}
            >
              <SelectTrigger aria-label="Porção da Torá">
                <SelectValue placeholder="Parashá" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas as Parashiot</SelectItem>
                {options?.parashiot.map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.number}. {p.name_translit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Ano do calendário judaico
            </label>
            <Select
              value={filters.hebrewYear?.toString() ?? ALL}
              onValueChange={(v) => update({ hebrewYear: v === ALL ? undefined : parseInt(v, 10) })}
            >
              <SelectTrigger aria-label="Ano do calendário judaico">
                <SelectValue placeholder="Ex.: 5786" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos os anos hebraicos</SelectItem>
                {options?.hebrewYears.map((y: number) => (
                  <SelectItem key={y} value={y.toString()}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Ano do calendário gregoriano
            </label>
            <Select
              value={filters.gregYear?.toString() ?? ALL}
              onValueChange={(v) =>
                update({
                  gregYear: v === ALL ? undefined : parseInt(v, 10),
                  gregMonth: v === ALL ? undefined : filters.gregMonth,
                })
              }
            >
              <SelectTrigger aria-label="Ano do calendário gregoriano">
                <SelectValue placeholder="Ex.: 2026" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos os anos</SelectItem>
                {options?.gregYears?.map((g: any) => (
                  <SelectItem key={g.year} value={g.year.toString()}>
                    {g.year}
                    {g.hebrew ? ` (${g.hebrew})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Mês {filters.gregYear ? "" : "(escolha o ano primeiro)"}
            </label>
            <Select
              value={filters.gregMonth?.toString() ?? ALL}
              onValueChange={(v) => update({ gregMonth: v === ALL ? undefined : parseInt(v, 10) })}
              disabled={!filters.gregYear}
            >
              <SelectTrigger aria-label="Mês">
                <SelectValue
                  placeholder={filters.gregYear ? "Todos os meses" : "Selecione o ano"}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos os meses</SelectItem>
                {MONTHS_PT.map((label, i) => (
                  <SelectItem key={i + 1} value={(i + 1).toString()}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Turno do estudo
            </label>
            <Select
              value={filters.turno ?? ALL}
              onValueChange={(v) => update({ turno: v === ALL ? undefined : (v as any) })}
            >
              <SelectTrigger aria-label="Turno do estudo">
                <SelectValue placeholder="Manhã e tarde" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Manhã e tarde</SelectItem>
                <SelectItem value="manha">Manhã (11h)</SelectItem>
                <SelectItem value="tarde">Tarde (15h30)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Festividade
            </label>
            <Select
              value={filters.festivitySlug ?? ALL}
              onValueChange={(v) => update({ festivitySlug: v === ALL ? undefined : v })}
            >
              <SelectTrigger aria-label="Festividade">
                <SelectValue placeholder="Nenhum filtro" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Nenhum (sem filtro)</SelectItem>
                <SelectItem value="__any__">Todas as festividades</SelectItem>
                <SelectItem value="__none__">Sem festividade</SelectItem>
                {options?.festivities.map((f: any) => (
                  <SelectItem key={f.slug} value={f.slug}>
                    {f.name_pt}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {data
              ? `${data.count} ensino${data.count === 1 ? "" : "s"} encontrado${data.count === 1 ? "" : "s"}`
              : ""}
          </p>
          <div className="flex items-center gap-2">
            <Select value={filters.order} onValueChange={(v) => update({ order: v as any })}>
              <SelectTrigger aria-label="Ordenar resultados" className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="recent">Mais recentes</SelectItem>
                <SelectItem value="oldest">Mais antigos</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearAll}>
                <X className="mr-1 h-4 w-4" /> Limpar
              </Button>
            )}
          </div>
        </div>
      </div>

      <h2 className="sr-only">Resultados</h2>
      {isLoading ? (
        <div className="grid min-h-[70vh] gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-72 animate-pulse rounded-xl bg-secondary" />
          ))}
        </div>
      ) : (visibleRows.length ?? 0) === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-16 text-center">
          <p className="text-muted-foreground">
            {hideWatched && (data?.rows.length ?? 0) > 0
              ? "Todos os ensinos desta página já foram assistidos."
              : "Nenhum ensino encontrado com esses filtros."}
          </p>
        </div>
      ) : (
        <>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleRows.map((t: any) => (
              <TeachingCard key={t.id} teaching={t} />
            ))}
          </div>

          {data!.count > (filters.offset ?? 0) + (filters.limit ?? 24) && (
            <div className="flex justify-center pt-4">
              <Button
                variant="outline"
                onClick={() =>
                  setFilters((f) => ({ ...f, offset: (f.offset ?? 0) + (f.limit ?? 24) }))
                }
              >
                Carregar mais
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
